import {
  Activity,
  Baby,
  BadgeCheck,
  Beef,
  Blocks,
  Bone,
  BookOpen,
  Brain,
  Briefcase,
  Bug,
  Building2,
  Calculator,
  CalendarCheck,
  Camera,
  Car,
  ChefHat,
  CircleDot,
  Clock,
  Code,
  Coffee,
  Croissant,
  Dog,
  Droplets,
  Dumbbell,
  Eye,
  Facebook,
  FileCheck,
  Film,
  Fish,
  Flag,
  Flame,
  Footprints,
  Gamepad2,
  GitCompareArrows,
  Glasses,
  GraduationCap,
  Hammer,
  Hand,
  Handshake,
  HardHat,
  HeartPulse,
  Home,
  Inbox,
  Instagram,
  Key,
  KeyRound,
  LifeBuoy,
  LineChart,
  Linkedin,
  type LucideIcon,
  MapPin,
  Megaphone,
  MessageSquare,
  Monitor,
  PackageOpen,
  Paintbrush,
  PaintRoller,
  Palette,
  PartyPopper,
  PencilLine,
  PenTool,
  Phone,
  Pizza,
  Receipt,
  Salad,
  Scale,
  Scissors,
  Search,
  ShieldCheck,
  ShoppingBag,
  Smile,
  Sparkle,
  Sparkles,
  Square,
  Star,
  Stethoscope,
  Store,
  Target,
  TowerControl,
  Trees,
  TrendingUp,
  Truck,
  UserCheck,
  Users,
  UtensilsCrossed,
  Wind,
  Wine,
  Wrench,
  Zap,
} from 'lucide-react'

/**
 * `categories.icon` holds a LUCIDE NAME, so an administrator can change the mark
 * on a category without a deploy (CLAUDE.md hard rule 4, Docs/06 §7).
 *
 * A bundler cannot import by string — `lucide-react`'s dynamic map pulls the
 * entire icon set into the bundle — so every name the data may use is registered
 * here explicitly. The list below covers all 94 active categories plus the
 * marketing sections and social links.
 *
 * An unknown name is NOT an error. A category added in admin tomorrow with an
 * icon nobody registered must render with a neutral mark and a working link, not
 * take the grid down with it. `getIcon` therefore always returns a component.
 *
 * ─── CHECKED AGAINST THE LIVE DATA, 7 OCT (Docs/15 Q102) ────────────────────
 *
 * Counted as anon with `count=exact`: 94 active categories, 57 distinct icon
 * names, no nulls. **All 57 were already here** — the taxonomy going 67 → 94 on
 * 6 Oct did not open a hole on the web, only on mobile, whose registry held 18 of
 * the 57 and drew `Building2` for 49 categories.
 *
 * ⚠️ SO THE "CUISINES SHARE ONE ICON" COMPLAINT IS NOT A REGISTRY BUG HERE.
 * Fourteen food rows store the name `'UtensilsCrossed'` in the DATABASE and this
 * file maps that name correctly. Separating them is an UPDATE on
 * `categories.icon`, not an import. The six food marks at the end of the registry
 * are registered ahead of that update so it needs no deploy — which is the point
 * of an admin-editable icon column (hard rule 4). They are the only six cuisines
 * with an unambiguous lucide mark; the eight national cuisines have none and must
 * keep the neutral one. Kept in step with
 * `apps/mobile/src/features/businesses/lib/icons.ts`, which has the full note.
 *
 * ─── `Film` AND `Gamepad2`, 8 OCT (Docs/15 Q118) ────────────────────────────
 *
 * `kino` and `spillhall-og-esport` both stored `'Monitor'`, the mark of their
 * grandparent `it-og-media` — a cinema and an esports arcade drawn as an office
 * screen. Both now have their own name, and the ORDER MATTERS: these two lines
 * are registered BEFORE the migration that writes the names, because an
 * unregistered name renders `FALLBACK_ICON` — so writing the data first would
 * have replaced a wrong-but-deliberate mark with a generic one and looked like a
 * fix. Migration 065 excluded them for exactly this reason.
 *
 * `Monitor` still belongs to `it-og-media` and its child `it-support`, which is
 * parent-and-namesake sharing and is correct.
 */
const registry: Record<string, LucideIcon> = {
  Activity,
  Baby,
  BadgeCheck,
  Beef,
  Blocks,
  Bone,
  BookOpen,
  Brain,
  Briefcase,
  Bug,
  Building2,
  Calculator,
  CalendarCheck,
  Camera,
  Car,
  ChefHat,
  CircleDot,
  Clock,
  Code,
  Coffee,
  Croissant,
  Dog,
  Droplets,
  Dumbbell,
  Eye,
  Facebook,
  FileCheck,
  Film,
  Fish,
  Flag,
  Flame,
  Footprints,
  Gamepad2,
  GitCompareArrows,
  Glasses,
  GraduationCap,
  Hammer,
  Hand,
  Handshake,
  HardHat,
  HeartPulse,
  Home,
  Inbox,
  Instagram,
  Key,
  KeyRound,
  LifeBuoy,
  LineChart,
  Linkedin,
  MapPin,
  Megaphone,
  MessageSquare,
  Monitor,
  PackageOpen,
  Paintbrush,
  PaintRoller,
  Palette,
  PartyPopper,
  PenTool,
  PencilLine,
  Phone,
  Pizza,
  Receipt,
  Salad,
  Scale,
  Scissors,
  Search,
  ShieldCheck,
  ShoppingBag,
  Smile,
  Sparkle,
  Sparkles,
  Square,
  Star,
  Stethoscope,
  Store,
  Target,
  TowerControl,
  Trees,
  TrendingUp,
  Truck,
  UserCheck,
  Users,
  UtensilsCrossed,
  Wind,
  Wine,
  Wrench,
  Zap,
}

/** The mark used when `categories.icon` is null or names an icon we do not have. */
export const FALLBACK_ICON: LucideIcon = Sparkles

export const getIcon = (name: string | null | undefined): LucideIcon =>
  (name ? registry[name] : undefined) ?? FALLBACK_ICON
